import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import {
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  CustomCheckbox,
  StatusChip,
} from '../../../common';

const formatPeriodLabel = (month, year) => {
  if (!month || !year) {
    return 'Periodo N/D';
  }

  const normalizedMonth = String(month).padStart(2, '0');
  return `Periodo ${normalizedMonth}/${year}`;
};

const formatFeeLabel = (feeNumber1, feeNumber2) => {
  const first = Number(feeNumber1);
  const second = feeNumber2 !== undefined && feeNumber2 !== null ? Number(feeNumber2) : null;

  if (Number.isNaN(first)) {
    return null;
  }

  if (first === 0) {
    return 'Adecuación';
  }

  if (!second || second === 0 || second === first) {
    return `Cuota ${first}`;
  }

  return `Cuotas ${first}-${second}`;
};

const buildCouponLabel = (coupon) => {
  const period = formatPeriodLabel(coupon.month, coupon.year);
  const fee = formatFeeLabel(coupon.fee_number1, coupon.fee_number2);
  return fee ? `${period} • ${fee}` : period;
};

const CouponsMailsDataTable = ({
  data,
  isLoading,
  includeAllSubscribers,
  totalCount,
  bulkTotalCount,
  isAllSubscribersLoading,
  selectedItems,
  onSendEmail,
  onSelectItem,
  LoadingComponent,
}) => {
  const hasRows = data && data.length > 0;

  const getMailingItemKey = (item) => {
    const subscriberKey = item.subscriber_uuid || '';
    const contractKey = item.contract?.uuid || item.contract_number || '';
    return `${subscriberKey}-${contractKey}`;
  };

  if (includeAllSubscribers) {
    return (
      <GeneralTableBox
        hasMaxHeight={false}
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 4 },
            py: { xs: 4, md: 5 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            gap: 2,
            justifyContent: 'center',
            minHeight: 180,
            textAlign: { xs: 'center', sm: 'left' },
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'rgba(94, 137, 79, 0.12)',
              color: 'primary.main',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            {isAllSubscribersLoading ? (
              <CircularProgress size={26} color="inherit" />
            ) : (
              <CheckCircleOutlineIcon fontSize="large" />
            )}
          </Box>

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.5 }}>
              {isAllSubscribersLoading
                ? 'Preparando la lista completa de destinatarios'
                : 'Todos los destinatarios del filtro quedaron seleccionados'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isAllSubscribersLoading
                ? `Buscando el lote completo sobre ${totalCount} registro(s) filtrados.`
                : `Total listo para confirmar: ${bulkTotalCount || totalCount} suscriptor(es).`}
            </Typography>
          </Box>
        </Box>
      </GeneralTableBox>
    );
  }

  const tableMinWidth = hasRows || isLoading ? 980 : '100%';

  return (
    <GeneralTableBox
      sx={{
        overflowX: hasRows || isLoading ? 'auto' : 'hidden',
        '& table': {
          width: '100%',
          minWidth: tableMinWidth,
          tableLayout: hasRows || isLoading ? 'auto' : 'fixed',
        },
      }}
    >
      <Table
        aria-label="coupons mails table"
        sx={{
          width: '100%',
          minWidth: tableMinWidth,
          '& th, & td': {
            py: 1.25,
          },
        }}
      >
        <TableHead>
          <TableRow>
            <HeadTableCell wmd="10%" wxs="15%">
              Contrato
            </HeadTableCell>
            <HeadTableCell wmd="26%" wxs="28%">
              Suscriptor
            </HeadTableCell>
            <HeadTableCell wmd="44%" wxs="37%">
              Cupones
            </HeadTableCell>
            <HeadTableCell wmd="10%" wxs="10%">
              Enviar
            </HeadTableCell>
            <HeadTableCell wmd="10%" wxs="10%">
              Seleccionar
            </HeadTableCell>
          </TableRow>
        </TableHead>
        {(isLoading || hasRows) && (
          <TableBody>
            {isLoading ? (
              <LoadingComponent />
            ) : (
              data.map((subscriber, key) => {
                const itemKey = getMailingItemKey(subscriber);
                const isSelected = selectedItems.some(
                  (item) => getMailingItemKey(item) === itemKey
                );

                return (
                  <TableRow
                    key={itemKey || key}
                    selected={isSelected}
                    sx={{
                      '&.Mui-selected': {
                        bgcolor: 'rgba(2, 136, 209, 0.12)',
                      },
                      '&.Mui-selected:hover': {
                        bgcolor: 'rgba(2, 136, 209, 0.18)',
                      },
                      '&:hover': {
                        bgcolor: 'rgba(2, 136, 209, 0.08)',
                      },
                    }}
                  >
                    <RowTableCell fontxs="12px" fontmd="14px" sx={{ minWidth: 120 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <AssignmentOutlinedIcon fontSize="small" color="primary" />
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {subscriber.contract_number}
                        </Typography>
                      </Stack>
                    </RowTableCell>

                    <RowTableCell
                      fontxs="12px"
                      fontmd="14px"
                      sx={{ minWidth: 260, textAlign: 'left' }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}
                      >
                        {`${subscriber.first_name} ${subscriber.last_name}`}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {subscriber.email || subscriber.subscriber?.email || 'Sin email registrado'}
                      </Typography>
                    </RowTableCell>

                    <RowTableCell sx={{ minWidth: 420, whiteSpace: 'normal' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.75,
                          fontSize: { xs: '12px', md: '14px' },
                          justifyContent: 'center',
                        }}
                      >
                        {subscriber.coupons.map((coupon, index) => (
                          <StatusChip
                            key={`${coupon.uuid || index}`}
                            variant="filled"
                            status="neutral"
                            label={buildCouponLabel(coupon)}
                            size="small"
                            sx={{ letterSpacing: 0 }}
                          />
                        ))}
                      </Box>
                    </RowTableCell>

                    <RowTableCell>
                      <Tooltip title="Enviar email individual">
                        <IconButton
                          size="small"
                          onClick={() => onSendEmail(subscriber)}
                          sx={{
                            color: 'primary.main',
                            bgcolor: 'rgba(94, 137, 79, 0.08)',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              bgcolor: 'rgba(94, 137, 79, 0.16)',
                            },
                          }}
                        >
                          <EmailOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    </RowTableCell>

                    <RowTableCell>
                      <CustomCheckbox
                        checked={isSelected}
                        onChange={() => onSelectItem(subscriber)}
                        label=""
                        sx={{ padding: '4px' }}
                        inputProps={{
                          'aria-label': `Seleccionar contrato ${subscriber.contract_number}`,
                        }}
                      />
                    </RowTableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        )}
      </Table>

      {!isLoading && !hasRows && (
        <Box
          sx={{
            width: '100%',
            minHeight: 180,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'primary.main',
            px: 2,
            py: 4,
          }}
        >
          <Stack
            spacing={1.5}
            alignItems="center"
            justifyContent="center"
            sx={{
              width: '100%',
              textAlign: 'center',
              opacity: 0.6,
            }}
          >
            <SearchOffIcon
              sx={{
                fontSize: { xs: 48, md: 64 },
                color: 'text.secondary',
              }}
            />
            <Typography
              variant="h6"
              sx={{
                width: '100%',
                fontSize: { xs: 16, md: 18 },
                color: 'text.secondary',
                fontWeight: 700,
                textAlign: 'center',
              }}
            >
              No se encontraron cupones para enviar por email
            </Typography>
            <Typography
              variant="body2"
              sx={{
                width: '100%',
                fontSize: { xs: 12, md: 14 },
                color: 'text.disabled',
                textAlign: 'center',
              }}
            >
              Intenta ajustar los filtros o verifica que existan registros para mostrar.
            </Typography>
          </Stack>
        </Box>
      )}
    </GeneralTableBox>
  );
};

export default CouponsMailsDataTable;
